import { Controller } from "@hotwired/stimulus"
import { log } from "../../../ui/static_src/ui/utils/logging.js"

const PooolConsentDelay = 200
const PooolDebug = true

class Paywall extends Controller {
  static targets = [
    "wall",
    "container",
    "banner",
    // details
    "section",
    // lists
    "main",
    "toggleEnabledButton",
    "trialBlock",
    // poool
    "content",
  ]

  static outlets = ["footer"]
  static values = {
    type: String,
    extra: String,
  }

  // Lists
  threshold = 500
  enabled = true
  active = false
  pageScrollTop = 0

  // Base
  footerParent = undefined

  isList() { return this.typeValue === "list" }

  isDetail() { return this.typeValue === "detail" }

  appendFooter() {
    const footer = this.footerOutletElement
    this.footerParent = footer.parentElement
    this.wallTarget.appendChild(footer)
    // todo what is this `-locked` modifier?
    footer.classList.remove("-locked")
  }

  detachFooter() {
    this.footerParent.appendChild(this.footerOutletElement)
  }

  connect() {
    // Defer the execution of appendFooter until after the current call stack has cleared.
    // This allow time for other parts of the page to initialize
    setTimeout(() => {
      this.appendFooter()
    }, 100)
    this.extra = JSON.parse(this.extraValue)
    this.threshold = this.extra.threshold ?? this.threshold

    if (this.shouldInit()) {
      this.initPoool()
    } else if (this.isDetail()) {
      this.showHidingPaywall()
    }

    if (this.isList()) {
      this._deactivate()
      document.body.style.setProperty("--js-paywall-lists-threshold", `${this.threshold}px`)

      const observer = new IntersectionObserver((entries, observer) => {
        if (!this.active) {
          return
        }
        entries.forEach((entry) => {
          if (entry.intersectionRatio < 1) {
            this._deactivate()
          }
        })
      }, { rootMargin: "50px 0px -50px 0px" },
      )
      observer.observe(this.containerTarget)
    }

    this.cookiesEnabled = true
  }

  showHidingPaywall() {
    let topOffset = this.sectionTarget.getBoundingClientRect().bottom
    topOffset = this.sectionTarget.classList.contains("briefitem-body") ? topOffset * 0.9 : topOffset
    this.containerTarget.style.marginTop = `${Math.min(
      topOffset,
      window.visualViewport.height - (this.containerTarget.offsetHeight / 4),
    )}px`
  }

  // Lists
  _activate(offset) {
    this.active = true

    let containerMarginTop = 0
    if (offset) {
      containerMarginTop = document.scrollingElement.scrollTop + offset
    } else {
      containerMarginTop = this.wallTarget.offsetTop + this.threshold
    }
    this.containerTarget.style.marginTop = `${containerMarginTop}px`
    this.wallTarget.classList.add("-active")

    // Limit main element height to match the paywall height
    const mainMargin = parseInt(getComputedStyle(this.mainTarget).marginTop)
    const paywallRect = this.wallTarget.getBoundingClientRect()
    const totalHeight = paywallRect.height - mainMargin + "px"
    this.mainTarget.style.maxHeight = totalHeight
    this.element.style.minHeight = totalHeight
    this.wallTarget.style.top = ""
  }

  _deactivate() {
    this.active = false
    this.wallTarget.classList.remove("-active")
    this.wallTarget.style.top = `calc(100vh - ${this.bannerTarget.offsetHeight}px)`
    this.containerTarget.style.marginTop = ""

    // Reset main element height
    this.mainTarget.style.maxHeight = null
    this.element.style.minHeight = null
  }

  _disable() {
    this._deactivate()
    this.enabled = false
    this.wallTarget.classList.add("-disabled")
    this.detachFooter()
  }

  _reenable() {
    this.enabled = true
    this.wallTarget.classList.remove("-disabled")
    this.appendFooter()
    this._activate(window.visualViewport.height - this.bannerTarget.offsetHeight)
    document.scrollingElement.scrollBy({ left: 0, top: this.trialBlockTarget.offsetHeight + this.bannerTarget.offsetHeight, behavior: "smooth" })
  }

  toggleEnabled() {
    if (this.enabled) {
      this._disable()
    } else {
      this._reenable()
    }
    this.toggleEnabledButtonTarget.classList.toggle("-reversed")
  }

  handlePageScroll() {
    if (this.isDetail()) return
    if (this.enabled) {
      if (!this.active) {
        const deltaY = window.scrollY - this.pageScrollTop
        if (deltaY > 0 && window.scrollY > this.threshold) {
          this._activate()
        }
      }
    }

    this.pageScrollTop = window.scrollY
  }

  // Poool
  initPoool() {
    // Listen for cookies constent
    window.addEventListener("cookies-consent:complete", this.consent.bind(this))

    // Load script
    const script = document.createElement("script")
    script.async = true
    script.src = "https://assets.poool.fr/access.min.js"
    script.onload = () => {
      this.createPaywall({ delay: PooolConsentDelay })
    }
    document.head.appendChild(script)

    return true
  }

  consent({ detail: choices }) {
    const hasConsent = choices.poool
    log("Poool consent " + (hasConsent ? "OK" : "KO"))
  }

  createPaywall(params) {
    const env = this.getEnv()
    const delay = params?.delay || 0

    // Check if Poool is loaded
    if (!window.Access) {
      return
    }

    // Clear consent timeout
    clearTimeout(this.consentTimeout)
    this.consentTimeout = null

    // Build config
    const config = {
      cookies_enabled: this.cookiesEnabled,
      debug: PooolDebug,
    }
    const { context, variables } = this.getContextAndVariables(env)
    log("Initializing Poool with config", config, "context", context, "variables", variables)

    // Delay if needed
    if (delay) {
      this.consentTimeout = setTimeout(() => {
        this.createPaywall()
      }, delay)

      log("Delaying Poool init for", delay, "ms")
      return
    }

    // Clear existing Poool paywall
    if (window.poool) {
      window.poool.destroy()
    }

    // Clear existing paywall content
    this.contentTarget.replaceChildren([])
    this.contentTarget.className = "poool-stage"

    // Init Poool paywall
    window.poool = window.Access
      .init(env.pooolAppId)
      .config(config)
      .config("context", context)
      .variables(variables)
      .createPaywall({
        target: this.contentTarget,
        mode: "custom",
        pageType: "premium",
      })
      .on("ready", event => {
        this.isDetail() && this.showHidingPaywall()
        log("Poool init OK")
      })
  }

  shouldInit() {
    if (!this.getEnv().pooolAppId) {
      log("Poool app ID not found, cancelling init")
      return false
    }
    if (window.Access) {
      log("Poool already loaded, cancelling init")
      return false
    }
    if (!this.hasContentTarget) {
      log("Poool content target not found, cancelling init")
      return false
    }
    return true
  }

  // --- Internal methods

  getEnv() {
    const env = document.querySelector("[data-poool-app-id]")?.dataset || {}
    env.paywallType = this.isList() ? "list" : "content"
    return env
  }

  getContextAndVariables(env) {
    // Poool context is provided via tracking.html (via UI context processor)
    const context = [
      env.paywallType + "_paywall",
      env.pageType + "_page",
      env.pageRegion + "_page_region",
      env.userRegions + "_user_regions",
      env.userLanguage + "_user_language",
    ]
    // Unlock status is provided via Poool variables (via PaywallMixin)
    const variables = this.getVariables(env)
    if (variables.unlock_status) {
      context.push(variables.unlock_status + "_content")
    }
    return { context, variables }
  }

  getVariables(env) {
    try {
      const encodedVariables = env.pooolVariables
      return JSON.parse(atob(encodedVariables))
    } catch (e) {
      return {}
    }
  }
}

export { Paywall }
